import { ComponentType, isValidElement, ReactElement, ReactNode } from "react";
import { makeResourceUrl } from "@animech/axiom-utility";
import { LogoData, theme, UiManagerDefinitions, UiSlotNodes } from "src/ui";
import { root } from "src/definitions";
import { UiStructure } from "src/common";

export function makeUiManagerDefinitions(
	ui: UiStructure,
	resourceBaseUrl: string,
): UiManagerDefinitions {
	const slotNodes: UiSlotNodes = {
		configurator:
			ui.settings?.layout === "sidebar" ? ui.slots.sidebar : ui.slots.bottomBar,
		mobileConfigurator: ui.slots.bottomBar,
		cart: ui.slots.cart,
		productInfo: ui.slots.productInfo,
		productPicker: ui.slots.productPicker,
	};
	const logos = convertLogos(ui, resourceBaseUrl);

	return {
		slotNodes,
		layout: ui.settings?.layout ?? "bottombar",
		logo: logos,
		initialActiveProperty: ui.settings?.initialActiveProperty ?? root,
	};
}

function convertLogos(ui: UiStructure, resourceBaseUrl: string): LogoData {
	return {
		configurator: {
			desktop: makeResourceUrl(ui.logos?.configurator.desktop, resourceBaseUrl),
			mobile: makeResourceUrl(ui.logos?.configurator.mobile, resourceBaseUrl),
		},
		overlay: {
			desktop: makeResourceUrl(ui.logos?.overlay.desktop, resourceBaseUrl),
			mobile: makeResourceUrl(ui.logos?.overlay.mobile, resourceBaseUrl),
		},
	};
}

export const isMobile = () =>
	window.innerWidth < parseInt(theme.breakpoints.tablet.replace("px", ""), 10);

export function isReactElementOfType<P>(
	node: ReactNode,
	type: ComponentType<P> | string,
): node is ReactElement<P> {
	return isValidElement(node) && node.type === type;
}

export function isHtmlElement(
	element: EventTarget | undefined | null,
): element is HTMLElement {
	return element instanceof HTMLElement;
}

export function isElementWithTag<K extends keyof HTMLElementTagNameMap>(
	element: EventTarget | undefined | null,
	...tagNames: K[]
): element is HTMLElementTagNameMap[K] {
	return (
		element instanceof Element &&
		tagNames.includes(element.tagName.toLowerCase() as K)
	);
}

export function isInputOrTextareaElement(
	element: EventTarget | undefined | null,
): element is HTMLInputElement | HTMLTextAreaElement {
	return (
		(isElementWithTag(element, "input") &&
			!["button", "checkbox", "radio", "image"].includes(element.type)) ||
		isElementWithTag(element, "textarea")
	);
}
